import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import DateTimePicker from "../../components/DateTimePicker";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";
import {
  generateOppositionHearingNoticePresignedUrls,
  getOppositionSubsequentHearing,
  openOppositionSubsequentHearingRound,
  type OpenOppositionSubsequentHearingItem,
  type OppositionSubsequentHearingItem,
} from "api/opposition";

export const OPPOSITION_SUBSEQUENT_HEARINGS_FORM_ID =
  "opposition-subsequent-hearings-form";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const READONLY_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] leading-5 text-[#6D6D6D] box-border";
const SECTION_TITLE_CLASS =
  "text-[12px] font-semibold uppercase tracking-wide text-[#6D6D6D]";

const HEARING_STATUS_LABEL: Record<string, string> = {
  ATTENDED: "Attended",
  NOT_ATTENDED: "Not Attended",
  DIDNT_HAPPEN: "Didn't Happen",
};

const HEARING_REMARK_LABEL: Record<string, string> = {
  ACCEPTED: "Accepted",
  REFUSED: "Refused",
  WILL_PASS_ON_MERITS: "Will pass on merits",
  FILE_TM_M: "File TM-M",
  DIRECTION_OF_OFFICER: "Direction of Officer",
};

const HEARING_ORDINAL: Record<number, string> = {
  2: "2nd",
  3: "3rd",
  4: "4th",
  5: "5th",
  6: "6th",
  7: "7th",
};

function ordinal(n: number): string {
  return HEARING_ORDINAL[n] ?? `${n}th`;
}

/** Serialize Date to YYYY-MM-DD for backend. */
function toIsoDateString(date: Date | null): string | null {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

type Draft = {
  nextRoundDate: Date | null;
  pendingNoticeFile: File | null;
};

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionSubsequentHearingsView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [opening, setOpening] = useState(false);
  const [items, setItems] = useState<OppositionSubsequentHearingItem[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOppositionSubsequentHearing(leadId)
      .then((data) => {
        if (cancelled) return;
        setItems(data.items ?? []);
        const next: Record<string, Draft> = {};
        for (const it of data.items ?? []) {
          next[it.conflictingMarkId] = {
            nextRoundDate: null,
            pendingNoticeFile: null,
          };
        }
        setDrafts(next);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Subsequent Opposition Hearing"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const updateDraft = (markId: string, patch: Partial<Draft>) => {
    setDrafts((prev) => ({
      ...prev,
      [markId]: { ...prev[markId], ...patch },
    }));
  };

  const handlePickFile = (markId: string, file: File | null) => {
    if (!file) {
      updateDraft(markId, { pendingNoticeFile: null });
      return;
    }
    if (file.size === 0) {
      toast.error("Selected file is empty.");
      return;
    }
    updateDraft(markId, { pendingNoticeFile: file });
  };

  const handleDownloadRoundNotice = async (
    url: string | null,
    filename: string | null
  ) => {
    if (!url) return;
    try {
      const result = await downloadFileFromUrlWithFallback(
        url,
        filename ?? "hearing-notice.pdf"
      );
      if (result === "local") toast.success("PDF saved to your device.");
      else toast.info("Opened the PDF in a new tab.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    }
  };

  const eligibleItems = items.filter((it) => it.canOpenNextRound);
  const hasAnyRound = items.some((it) => it.currentRound > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || opening) return;

    // Spec: when no application needs a subsequent hearing, skip directly
    // to step 61 (Final Order to Client).
    if (eligibleItems.length === 0) {
      onSuccess?.(leadId);
      return;
    }

    setOpening(true);
    try {
      // 1) Upload any pending Hearing Notice PDFs (per eligible app)
      const uploads: Array<{ markId: string; file: File }> = [];
      for (const it of eligibleItems) {
        const d = drafts[it.conflictingMarkId];
        if (d?.pendingNoticeFile) {
          uploads.push({ markId: it.conflictingMarkId, file: d.pendingNoticeFile });
        }
      }
      const keyByMarkId: Record<string, { key: string; filename: string }> = {};
      if (uploads.length > 0) {
        const presigned = await generateOppositionHearingNoticePresignedUrls(
          leadId,
          uploads.map((u) => ({
            filename: u.file.name,
            mimeType: u.file.type || "application/pdf",
          }))
        );
        if (presigned.length !== uploads.length) {
          throw new Error("Failed to generate upload URLs.");
        }
        await Promise.all(
          uploads.map(async (u, i) => {
            const target = presigned[i];
            const res = await fetch(target.url, {
              method: "PUT",
              body: u.file,
              headers: {
                "Content-Type": u.file.type || "application/pdf",
              },
            });
            if (!res.ok) throw new Error(`Failed to upload ${u.file.name}`);
            keyByMarkId[u.markId] = {
              key: target.key,
              filename: u.file.name,
            };
          })
        );
      }

      // 2) POST /open for each eligible app, optionally carrying date + PDF
      const payload: OpenOppositionSubsequentHearingItem[] = eligibleItems.map(
        (it) => {
          const d = drafts[it.conflictingMarkId];
          const uploaded = keyByMarkId[it.conflictingMarkId];
          return {
            conflictingMarkId: it.conflictingMarkId,
            dateOfOppositionHearing: toIsoDateString(d?.nextRoundDate ?? null),
            ...(uploaded
              ? {
                  hearingNoticeFileKey: uploaded.key,
                  hearingNoticeFilename: uploaded.filename,
                }
              : {}),
          };
        }
      );

      const fresh = await openOppositionSubsequentHearingRound(leadId, {
        conflictingMarks: payload,
      });
      setItems(fresh.items ?? []);
      // Reset drafts for next round entry
      const next: Record<string, Draft> = {};
      for (const it of fresh.items ?? []) {
        next[it.conflictingMarkId] = {
          nextRoundDate: null,
          pendingNoticeFile: null,
        };
      }
      setDrafts(next);
      toast.success(
        `Subsequent Opposition Hearing round opened for ${payload.length} application(s).`
      );
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to open subsequent round"
      );
    } finally {
      setOpening(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <form
        id={OPPOSITION_SUBSEQUENT_HEARINGS_FORM_ID}
        onSubmit={handleSubmit}
        className="flex w-full max-w-[1085px] flex-col gap-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="flex flex-col gap-2 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-4 py-6">
          <p className="text-sm font-semibold text-[#222]">
            No conflicting applications recorded
          </p>
          <p className="text-[12px] leading-5 text-[#6D6D6D]">
            Save the Opposition Status Details first before opening a
            subsequent hearing.
          </p>
        </div>
      </form>
    );
  }

  if (eligibleItems.length === 0) {
    return (
      <form
        id={OPPOSITION_SUBSEQUENT_HEARINGS_FORM_ID}
        onSubmit={handleSubmit}
        className="flex w-full max-w-[1085px] flex-col gap-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="flex flex-col gap-2 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-4 py-6">
          <p className="text-sm font-semibold text-[#222]">
            No subsequent Opposition Hearing required
          </p>
          <p className="text-[12px] leading-5 text-[#6D6D6D]">
            {hasAnyRound
              ? "Every conflicting application has either reached a final outcome (Accepted / Refused / Will pass on merits) or is waiting on an earlier step. Continue to record the Final Order."
              : "Save the original Opposition Hearing (Step 26) for each application before opening a subsequent round."}
          </p>
        </div>

        {items.map((it) => (
          <PerApplicationRoundsSummary
            key={it.conflictingMarkId}
            item={it}
            onDownloadRoundNotice={handleDownloadRoundNotice}
          />
        ))}
      </form>
    );
  }

  return (
    <form
      id={OPPOSITION_SUBSEQUENT_HEARINGS_FORM_ID}
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col gap-1 rounded-[6px] border border-[#EEEEEE] bg-white px-4 py-3">
        <p className="text-[13px] font-semibold text-[#222]">
          Open next Opposition Hearing round
        </p>
        <p className="text-[11px] leading-5 text-[#6D6D6D]">
          {eligibleItems.length} of {items.length} application(s) are eligible
          for a subsequent hearing. Enter the new hearing date / Notice PDF
          below (both optional — they can also be filled in later via the
          existing Status / Attend Hearing screens once the new round is
          open).
        </p>
      </div>

      {items.map((it) => {
        const d = drafts[it.conflictingMarkId];
        const nextRoundNumber = it.currentRound + 1;
        const eligible = it.canOpenNextRound;
        return (
          <div
            key={it.conflictingMarkId}
            className="flex flex-col gap-3 rounded-[6px] border border-[#EEEEEE] bg-white p-4"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-col gap-0.5">
                <p className="text-[13px] font-semibold text-[#222]">
                  {formatTmNoForDisplay(it.applicationNo ?? "")} —{" "}
                  {it.conflictingMark ?? "—"}
                </p>
                <p className="text-[11px] text-[#6D6D6D]">
                  Latest recorded round:{" "}
                  <span className="text-[#222]">
                    {it.currentRound === 0 ? "—" : it.currentRound}
                  </span>
                </p>
              </div>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-[6px] text-[11px] ${
                  eligible
                    ? "bg-[#E8F5EE] text-[#1B7A3E]"
                    : "bg-[#F7F7F7] text-[#6D6D6D]"
                }`}
              >
                {eligible
                  ? `Eligible to open round ${nextRoundNumber}`
                  : "Not eligible"}
              </span>
            </div>

            <p className="text-[11px] leading-5 text-[#6D6D6D]">
              {it.eligibilityReason}
            </p>

            {it.rounds.length > 0 && (
              <div className="rounded-[6px] border border-[#EEEEEE] overflow-hidden">
                <table className="w-full border-collapse text-[11px]">
                  <thead className="bg-[#F7F7F7] text-[#6D6D6D]">
                    <tr>
                      <th className="px-2.5 py-2 text-left font-medium">
                        Round
                      </th>
                      <th className="px-2.5 py-2 text-left font-medium">
                        Date of Hearing
                      </th>
                      <th className="px-2.5 py-2 text-left font-medium">
                        Status
                      </th>
                      <th className="px-2.5 py-2 text-left font-medium">
                        Officer Remark
                      </th>
                      <th className="px-2.5 py-2 text-left font-medium">
                        Notice
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {it.rounds.map((r) => (
                      <tr
                        key={r.roundNumber}
                        className="border-t border-[#EEEEEE]"
                      >
                        <td className="px-2.5 py-2 text-[#222]">
                          {r.roundNumber}
                        </td>
                        <td className="px-2.5 py-2 text-[#222]">
                          {r.dateOfOppositionHearing ?? "—"}
                        </td>
                        <td className="px-2.5 py-2 text-[#222]">
                          {r.hearingStatus
                            ? (HEARING_STATUS_LABEL[r.hearingStatus] ??
                              r.hearingStatus)
                            : "—"}
                        </td>
                        <td className="px-2.5 py-2 text-[#222]">
                          {r.remarkOfHearingOfficer
                            ? (HEARING_REMARK_LABEL[r.remarkOfHearingOfficer] ??
                              r.remarkOfHearingOfficer)
                            : "—"}
                        </td>
                        <td className="px-2.5 py-2">
                          {r.hearingNoticeDownloadUrl ? (
                            <button
                              type="button"
                              onClick={() =>
                                void handleDownloadRoundNotice(
                                  r.hearingNoticeDownloadUrl,
                                  r.hearingNoticeFilename
                                )
                              }
                              className="text-[#1B7A3E] underline"
                            >
                              {r.hearingNoticeFilename ?? "Download"}
                            </button>
                          ) : (
                            <span className="text-[#6D6D6D]">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {eligible && (
              <div className="flex flex-col gap-3 rounded-[6px] border border-[#EEEEEE] bg-[#FAFAFA] p-3">
                <p className={SECTION_TITLE_CLASS}>
                  Open round {nextRoundNumber} — {ordinal(nextRoundNumber)}{" "}
                  Opposition Hearing
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_CLASS}>
                      Date of Opposition Hearing {ordinal(nextRoundNumber)}{" "}
                      <span className="text-[#9A9A9A]">(optional)</span>
                    </label>
                    <DateTimePicker
                      value={d?.nextRoundDate ?? null}
                      onChange={(date) =>
                        updateDraft(it.conflictingMarkId, {
                          nextRoundDate: date,
                        })
                      }
                      showTime={false}
                      placeholder="Select Date"
                      buttonClassName="!h-9 !min-h-0 !text-[12px] rounded-[6px]"
                      readOnly={opening}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className={LABEL_CLASS}>
                      Hearing Notice (PDF){" "}
                      <span className="text-[#9A9A9A]">(optional)</span>
                    </label>
                    <div className="flex flex-col sm:flex-row flex-wrap gap-2.5">
                      <input
                        ref={(el) => {
                          fileInputRefs.current[it.conflictingMarkId] = el;
                        }}
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0] ?? null;
                          handlePickFile(it.conflictingMarkId, f);
                          const ref =
                            fileInputRefs.current[it.conflictingMarkId];
                          if (ref) ref.value = "";
                        }}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          fileInputRefs.current[
                            it.conflictingMarkId
                          ]?.click()
                        }
                        disabled={opening}
                        className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#222] bg-white border border-[#EEEEEE] rounded-[6px] hover:bg-[#F7F7F7] disabled:opacity-60"
                      >
                        {d?.pendingNoticeFile ? "Replace PDF" : "Upload PDF"}
                      </button>
                    </div>
                    {d?.pendingNoticeFile && (
                      <span className="text-[11px] leading-4 text-[#6D6D6D]">
                        New: {d.pendingNoticeFile.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </form>
  );
}

function PerApplicationRoundsSummary({
  item,
  onDownloadRoundNotice,
}: {
  item: OppositionSubsequentHearingItem;
  onDownloadRoundNotice: (
    url: string | null,
    filename: string | null
  ) => void | Promise<void>;
}) {
  if (item.rounds.length === 0) {
    return (
      <div className="flex flex-col gap-2 rounded-[6px] border border-[#EEEEEE] bg-white p-4">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Application</label>
          <input
            type="text"
            value={`${formatTmNoForDisplay(item.applicationNo ?? "")} — ${item.conflictingMark ?? "—"}`}
            readOnly
            className={READONLY_CLASS}
          />
        </div>
        <p className="text-[11px] leading-5 text-[#6D6D6D]">
          {item.eligibilityReason}
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3 rounded-[6px] border border-[#EEEEEE] bg-white p-4">
      <div className="flex flex-col gap-0.5">
        <p className="text-[13px] font-semibold text-[#222]">
          {formatTmNoForDisplay(item.applicationNo ?? "")} —{" "}
          {item.conflictingMark ?? "—"}
        </p>
        <p className="text-[11px] text-[#6D6D6D]">
          Latest recorded round:{" "}
          <span className="text-[#222]">{item.currentRound}</span>
        </p>
      </div>
      <div className="rounded-[6px] border border-[#EEEEEE] overflow-hidden">
        <table className="w-full border-collapse text-[11px]">
          <thead className="bg-[#F7F7F7] text-[#6D6D6D]">
            <tr>
              <th className="px-2.5 py-2 text-left font-medium">Round</th>
              <th className="px-2.5 py-2 text-left font-medium">
                Date of Hearing
              </th>
              <th className="px-2.5 py-2 text-left font-medium">Status</th>
              <th className="px-2.5 py-2 text-left font-medium">
                Officer Remark
              </th>
              <th className="px-2.5 py-2 text-left font-medium">Notice</th>
            </tr>
          </thead>
          <tbody>
            {item.rounds.map((r) => (
              <tr key={r.roundNumber} className="border-t border-[#EEEEEE]">
                <td className="px-2.5 py-2 text-[#222]">{r.roundNumber}</td>
                <td className="px-2.5 py-2 text-[#222]">
                  {r.dateOfOppositionHearing ?? "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222]">
                  {r.hearingStatus
                    ? (HEARING_STATUS_LABEL[r.hearingStatus] ??
                      r.hearingStatus)
                    : "—"}
                </td>
                <td className="px-2.5 py-2 text-[#222]">
                  {r.remarkOfHearingOfficer
                    ? (HEARING_REMARK_LABEL[r.remarkOfHearingOfficer] ??
                      r.remarkOfHearingOfficer)
                    : "—"}
                </td>
                <td className="px-2.5 py-2">
                  {r.hearingNoticeDownloadUrl ? (
                    <button
                      type="button"
                      onClick={() =>
                        void onDownloadRoundNotice(
                          r.hearingNoticeDownloadUrl,
                          r.hearingNoticeFilename
                        )
                      }
                      className="text-[#1B7A3E] underline"
                    >
                      {r.hearingNoticeFilename ?? "Download"}
                    </button>
                  ) : (
                    <span className="text-[#6D6D6D]">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] leading-5 text-[#6D6D6D]">
        {item.eligibilityReason}
      </p>
    </div>
  );
}
